// SNIJLAB CUSTOM FILE — custom JavaScript for the Snijlab theme.
// Loaded as an ES module from layout/theme.liquid after theme.js. Import theme classes with
// `import { ... } from "theme"` (importmap), never by asset URL. Keep theme.js untouched.

import { MegaMenuPromoCarousel } from "theme";
import { PhotoSwipeLightbox } from "vendor";

/* ------------------------------------------------------------------------------------------------
 * 1. Mega menu promo carousel: guard against a missing sibling / empty slide.
 *    Carried over from the 4.6.0 theme where this crashed when a promo slide had no controls.
 * ---------------------------------------------------------------------------------------------- */
MegaMenuPromoCarousel.prototype._updateControlsColor = function (slide) {
  if (!slide || !this.nextElementSibling) return;
  const extractFrom = slide.classList.contains("content-over-media") ? slide : slide.firstElementChild;
  if (!extractFrom) return;
  this.nextElementSibling.style.setProperty("--text-color", extractFrom.style.getPropertyValue("--text-color"));
};

/* ------------------------------------------------------------------------------------------------
 * 2. Live Product Options bridge (cloudlift app, used on "Graveren op eigen producten").
 *
 *    The app rewrites the ex VAT price in <sale-price> and the cart subtotal client-side. Our incl. VAT
 *    figures are rendered by Liquid (decision 0004) and are therefore correct after every Shopify
 *    re-render, but not after the app changes a price. This bridge recomputes them from the ex VAT text.
 *
 *    It only ever writes a value that differs from what is shown, so it cannot loop with its own
 *    MutationObserver, and it re-arms itself after product:rerender and cart re-renders.
 * ---------------------------------------------------------------------------------------------- */
const VAT_FACTOR = 1.21;

function moneyFormat() {
  return (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat)
    || (window.theme && window.theme.moneyFormat)
    || "€{{amount_with_comma_separator}}";
}

function formatMoney(cents, format) {
  const value = cents / 100;
  const parts = (decimals, thousands, decimal) => {
    const fixed = value.toFixed(decimals);
    const [int, frac] = fixed.split(".");
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac !== undefined ? grouped + decimal + frac : grouped;
  };
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = format.match(placeholder);
  const kind = match ? match[1] : "amount";
  let out;
  switch (kind) {
    case "amount_no_decimals": out = parts(0, ",", "."); break;
    case "amount_with_comma_separator": out = parts(2, ".", ","); break;
    case "amount_no_decimals_with_comma_separator": out = parts(0, ".", ","); break;
    case "amount_with_apostrophe_separator": out = parts(2, "'", "."); break;
    case "amount_no_decimals_with_space_separator": out = parts(0, " ", ","); break;
    case "amount_with_space_separator": out = parts(2, " ", ","); break;
    default: out = parts(2, ",", ".");
  }
  return format.replace(placeholder, out);
}

// "€ 1.234,56" / "€1,234.56" / "1234.56 EUR" -> cents
function parseMoneyText(text) {
  const cleaned = text.replace(/[^\d.,-]/g, "");
  if (!cleaned) return NaN;
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  let normalized;
  if (lastComma > lastDot) normalized = cleaned.replace(/\./g, "").replace(",", ".");
  else normalized = cleaned.replace(/,/g, "");
  const n = parseFloat(normalized);
  return isNaN(n) ? NaN : Math.round(n * 100);
}

function setText(el, text) {
  if (el && el.textContent.trim() !== text) el.textContent = text;
}

function updateProductPrices() {
  document.querySelectorAll("price-list").forEach((list) => {
    const untaxed = list.querySelector("sale-price");
    const taxed = list.querySelector("taxed-price");
    if (!untaxed || !taxed) return;
    const cents = parseMoneyText(untaxed.textContent);
    if (isNaN(cents)) return;
    setText(taxed, formatMoney(Math.round(cents * VAT_FACTOR), moneyFormat()));
  });
}

function updateCartTotals() {
  const untaxed = document.querySelector(".cart-total-untaxed");
  if (!untaxed) return;
  const cents = parseMoneyText(untaxed.textContent);
  if (isNaN(cents)) return;
  const taxedCents = Math.round(cents * VAT_FACTOR);
  setText(document.querySelector(".cart-total-taxed"), formatMoney(taxedCents, moneyFormat()));
  setText(document.querySelector(".cart-total-tax"), formatMoney(taxedCents - cents, moneyFormat()));
}

let scheduled = false;
function scheduleUpdate() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    updateProductPrices();
    updateCartTotals();
  });
}

const liveProductOptionsActive = () => !!document.querySelector('script[src*="cloudlift.app"]');

if (liveProductOptionsActive()) {
  new MutationObserver(scheduleUpdate).observe(document.body, { childList: true, subtree: true, characterData: true });
  ["product:rerender", "cart:refresh", "cart:change", "variant:change"].forEach((name) =>
    document.addEventListener(name, scheduleUpdate, true)
  );
  scheduleUpdate();
}

/* ------------------------------------------------------------------------------------------------
 * 3. Material buybox: swap variant-bound values on variant change.
 *
 *    The quick specs and price indication blocks (snippets/snijlab-product-*.liquid) are custom block
 *    types, and Impact's <product-rerender> only re-renders its own block types. Each block therefore
 *    embeds a JSON map { variantId: { rowKey: html } } and this handler swaps the values when the
 *    variant picker fires variant:change (bubbles from the product form). An empty value hides its row.
 * ---------------------------------------------------------------------------------------------- */
function applySnijlabVariantValues(container, variantId) {
  const script = container.querySelector("script[data-variant-values]");
  if (!script) return;
  let map;
  try {
    map = JSON.parse(script.textContent);
  } catch (e) {
    return;
  }
  const values = map[String(variantId)] || {};
  container.querySelectorAll("[data-row]").forEach((row) => {
    const value = values[row.dataset.row];
    const target = row.querySelector("[data-value]") || row;
    if (value === undefined || value === null || value === "") {
      if (row.hasAttribute("data-keep")) {
        // The row carries fixed text (price indication): keep it, only clear the variant-bound part.
        if (target !== row) target.innerHTML = "";
        row.hidden = false;
      } else {
        row.hidden = true;
      }
    } else {
      row.hidden = false;
      // Values are HTML-escaped by Liquid in the snippet that produced the JSON.
      if (target.innerHTML !== value) target.innerHTML = value;
    }
  });
}

document.addEventListener("variant:change", (event) => {
  const detail = event.detail || {};
  if (!detail.variant || !detail.formId) return;
  // Blocks inside the product section carry the form id; sections elsewhere on the page (specs) have none and follow any form.
  document
    .querySelectorAll(`[data-snijlab-variant-values][data-form-id="${detail.formId}"], [data-snijlab-variant-values]:not([data-form-id])`)
    .forEach((container) => applySnijlabVariantValues(container, detail.variant.id));

  // Material gallery: the new variant image moves to the first slot (the gallery itself is not re-rendered)
  const featuredId = detail.variant.featured_media && detail.variant.featured_media.id;
  if (featuredId) {
    document.querySelectorAll(".snijlab-gallery").forEach((gallery) => {
      const items = Array.from(gallery.querySelectorAll(".product-gallery__media"));
      const thumbs = Array.from(gallery.querySelectorAll(".product-gallery__thumbnail"));
      items.forEach((item, index) => {
        const on = item.getAttribute("data-media-id") === String(featuredId);
        item.classList.toggle("is-variant-media", on);
        if (thumbs[index]) thumbs[index].classList.toggle("is-variant-media", on);
      });
    });
  }

  // Anchor nav: "POM (Polyacetaal) · zwart · 3.0 mm"
  document.querySelectorAll("[data-snijlab-variant-title]").forEach((el) => {
    const productTitle = el.getAttribute("data-product-title") || "";
    const variantTitle = (detail.variant.title || "").replace(/ \/ /g, " · ");
    el.textContent = variantTitle ? `${productTitle} · ${variantTitle}` : productTitle;
  });
});

/* ------------------------------------------------------------------------------------------------
 * 4. Anchor nav: hide links whose target is not on this page. Sections hide themselves when they have
 *    no content (design tips without tips, applications without images), which would otherwise leave
 *    a link that jumps nowhere.
 * ---------------------------------------------------------------------------------------------- */
function pruneSnijlabAnchorNav() {
  const links = Array.from(document.querySelectorAll(".material-anchor-nav__link"));
  if (links.length === 0) return;

  // link.hash comes from the parsed URL, so stray whitespace in the attribute cannot break the lookup.
  const targets = links.map((link) => {
    const id = decodeURIComponent((link.hash || "").slice(1));
    return id ? document.getElementById(id) : null;
  });

  // If nothing resolves, the assumption behind this function is wrong. Leave the nav as it is rather
  // than hiding every link.
  if (!targets.some(Boolean)) return;

  links.forEach((link, index) => {
    link.hidden = !targets[index];
  });
}

document.addEventListener("DOMContentLoaded", pruneSnijlabAnchorNav);
document.addEventListener("shopify:section:load", pruneSnijlabAnchorNav);
document.addEventListener("shopify:section:unload", pruneSnijlabAnchorNav);

/* ------------------------------------------------------------------------------------------------
 * 5. Trustpilot widget in the trust row. The bootstrap script (header group) initialises the widgets it
 *    finds at load; anything that appears later (product re-render, section reload in the editor) needs
 *    an explicit loadFromElement.
 * ---------------------------------------------------------------------------------------------- */
function initSnijlabTrustpilot() {
  if (!window.Trustpilot || typeof window.Trustpilot.loadFromElement !== "function") return;
  document.querySelectorAll(".material-trust-row__trustpilot").forEach((el) => {
    if (el.querySelector("iframe")) return;
    window.Trustpilot.loadFromElement(el, true);
  });
}

window.addEventListener("load", () => setTimeout(initSnijlabTrustpilot, 500));
document.addEventListener("product:rerender", () => setTimeout(initSnijlabTrustpilot, 100), true);
document.addEventListener("shopify:section:load", () => setTimeout(initSnijlabTrustpilot, 100));

/* ------------------------------------------------------------------------------------------------
 * 6. Zoomable images outside the product gallery (design tips, and any other section that renders
 *    snippets/snijlab-zoomable-image.liquid).
 *
 *    Uses the same PhotoSwipe build and the same close button as the stock product gallery, so the
 *    overlay looks identical. Every zoomable image inside one [data-snijlab-lightbox] element forms a
 *    gallery, so the visitor can page through the tips without closing the overlay. The lightbox is
 *    built on first use: pages without a zoomable image never load PhotoSwipe.
 * ---------------------------------------------------------------------------------------------- */
const SNIJLAB_CLOSE_SVG = `<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
  <path d="M56 28C56 12.536 43.464 0 28 0S0 12.536 0 28s12.536 28 28 28 28-12.536 28-28Z" fill="#fff"/>
  <path d="M55.5 28C55.5 12.812 43.188.5 28 .5S.5 12.812.5 28 12.812 55.5 28 55.5 55.5 43.188 55.5 28Z" stroke="#252627" stroke-opacity=".12"/>
  <path d="m22.344 22.343 11.313 11.314m-11.313 0 11.313-11.313" stroke="#252627" stroke-width="2"/>
</svg>`;

let snijlabLightbox = null;

function getSnijlabLightbox() {
  if (snijlabLightbox) return snijlabLightbox;
  const strings = (window.themeVariables && window.themeVariables.strings) || {};
  snijlabLightbox = new PhotoSwipeLightbox({
    pswpModule: () => import("photoswipe"),
    bgOpacity: 1,
    maxZoomLevel: 3,
    closeTitle: strings.closeGallery,
    errorMsg: strings.errorGallery,
    arrowPrev: true,
    arrowNext: true,
    counter: true,
    zoom: false,
    closeSVG: SNIJLAB_CLOSE_SVG,
  });
  snijlabLightbox.addFilter("thumbEl", (thumbEl, data) => data.thumbnailElement || thumbEl);
  snijlabLightbox.init();
  return snijlabLightbox;
}

document.addEventListener("click", (event) => {
  const trigger = event.target.closest(".snijlab-zoomable");
  if (!trigger || !trigger.dataset.zoomSrc) return;
  event.preventDefault();

  const group = trigger.closest("[data-snijlab-lightbox]");
  const triggers = group
    ? Array.from(group.querySelectorAll(".snijlab-zoomable[data-zoom-src]")).filter(
        (el) => !el.closest("[hidden]")
      )
    : [trigger];

  const dataSource = triggers.map((el) => {
    const thumbnail = el.querySelector("img");
    return {
      thumbnailElement: thumbnail,
      src: el.dataset.zoomSrc,
      msrc: (thumbnail && (thumbnail.currentSrc || thumbnail.src)) || el.dataset.zoomSrc,
      width: parseInt(el.dataset.zoomWidth, 10) || undefined,
      height: parseInt(el.dataset.zoomHeight, 10) || undefined,
      alt: el.dataset.zoomAlt || "",
      thumbCropped: true,
    };
  });

  getSnijlabLightbox().loadAndOpen(Math.max(0, triggers.indexOf(trigger)), dataSource);
});

/* ------------------------------------------------------------------------------------------------
 * 7. Application photos (sections/material-applications.liquid).
 *
 *    The section renders every photo once and marks its role — hero, side, filmstrip, or beyond the
 *    filmstrip. Filtering on a tag re-runs exactly those rules on what is left, so the first matching
 *    photo becomes the hero and the block keeps its shape whether two or forty photos survive. Photos
 *    beyond the filmstrip stay in the page but out of sight: they are what the "bekijk alle" tile opens.
 * ---------------------------------------------------------------------------------------------- */
const SNIJLAB_APP_ROLES = ["is-hero", "is-side", "is-strip", "is-overflow"];
const SNIJLAB_APP_SIDES = 2;

function layoutSnijlabApplications(root) {
  const grid = root.querySelector("[data-app-grid]");
  const strip = root.querySelector("[data-app-strip]");
  const tile = root.querySelector("[data-app-showall]");
  if (!grid || !strip) return;

  const stripMax = parseInt(root.dataset.stripMax, 10) || 7;
  const filter = root.dataset.appFilter || "";
  const items = Array.from(root.querySelectorAll("[data-app-item]"));
  const visible = items.filter((el) => !filter || el.dataset.appTag === filter);
  const visibleSet = new Set(visible);

  items.forEach((el) => {
    el.hidden = !visibleSet.has(el);
  });

  visible.forEach((el, index) => {
    el.classList.remove(...SNIJLAB_APP_ROLES);
    if (index === 0) {
      el.classList.add("is-hero");
      grid.append(el);
    } else if (index <= SNIJLAB_APP_SIDES) {
      el.classList.add("is-side");
      grid.append(el);
    } else {
      el.classList.add(index <= SNIJLAB_APP_SIDES + stripMax ? "is-strip" : "is-overflow");
      strip.insertBefore(el, tile);
    }
  });

  root.classList.remove("count-1", "count-2", "count-3");
  root.classList.add(`count-${Math.min(3, Math.max(1, visible.length))}`);

  const overflow = Math.max(0, visible.length - 1 - SNIJLAB_APP_SIDES - stripMax);
  strip.hidden = visible.length <= 1 + SNIJLAB_APP_SIDES;
  if (tile) {
    tile.hidden = overflow === 0;
    const counter = tile.querySelector("[data-app-overflow]");
    if (counter) counter.textContent = `+${overflow}`;
  }
}

document.addEventListener("click", (event) => {
  const tag = event.target.closest("[data-app-tag][aria-pressed]");
  if (tag) {
    const root = tag.closest("[data-snijlab-applications]");
    if (!root) return;
    root.dataset.appFilter = tag.dataset.appTag || "";
    root.querySelectorAll("[data-app-tag][aria-pressed]").forEach((button) => {
      const active = button === tag;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });
    layoutSnijlabApplications(root);
    return;
  }

  // The tile has no photo of its own: it opens the gallery at the first one it hides.
  const showAll = event.target.closest("[data-app-showall]");
  if (showAll) {
    const root = showAll.closest("[data-snijlab-applications]");
    const first = root && root.querySelector(".is-overflow .snijlab-zoomable[data-zoom-src]");
    if (first) first.click();
  }
});

/* ------------------------------------------------------------------------------------------------
 * 8. Colour swatch label (snippets/snijlab-color-swatches.liquid).
 *
 *    The line above the swatches names the selected colour. Hovering or focusing a swatch shows that
 *    swatch's name (with "tijdelijk niet leverbaar" when it cannot be ordered); leaving it shows the
 *    selected colour again. Selecting is the theme's own radio change, which re-renders the picker, so
 *    the label is correct again from Liquid after every change.
 * ---------------------------------------------------------------------------------------------- */
function snijlabSwatchLabel(swatch) {
  const fieldset = swatch.closest("fieldset");
  return fieldset && fieldset.querySelector("[data-snijlab-swatch-current]");
}

function showSnijlabSwatchName(swatch) {
  const label = snijlabSwatchLabel(swatch);
  if (!label) return;
  const note = swatch.dataset.swatchNote;
  label.textContent = note ? `${swatch.dataset.swatchName} · ${note}` : swatch.dataset.swatchName;
}

function resetSnijlabSwatchName(swatch) {
  const label = snijlabSwatchLabel(swatch);
  if (label) label.textContent = label.dataset.selectedName || "";
}

document.addEventListener("pointerover", (event) => {
  const swatch = event.target.closest(".snijlab-swatch");
  if (swatch) showSnijlabSwatchName(swatch);
});

document.addEventListener("pointerout", (event) => {
  const swatch = event.target.closest(".snijlab-swatch");
  if (swatch && !swatch.contains(event.relatedTarget)) resetSnijlabSwatchName(swatch);
});

// The radio itself takes keyboard focus; its swatch is the label right after it
document.addEventListener("focusin", (event) => {
  const swatch = event.target.matches?.("input[data-option-position]") && event.target.nextElementSibling;
  if (swatch && swatch.classList.contains("snijlab-swatch")) showSnijlabSwatchName(swatch);
});

document.addEventListener("focusout", (event) => {
  const swatch = event.target.matches?.("input[data-option-position]") && event.target.nextElementSibling;
  if (swatch && swatch.classList.contains("snijlab-swatch")) resetSnijlabSwatchName(swatch);
});
